import { Alert, Badge, Button, Card, Skeleton } from '@personachat/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Compass, KeyRound, Smartphone, Unplug } from 'lucide-react';
import { useState } from 'react';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import { useSession } from '../../auth/AuthProvider';
import { desactiverDoubleAuth } from '../../auth/session';
import { Code, ErreurChargement, InfoList, Section } from '../../components/bits';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { API_URL } from '../../lib/env';
import { formatDateTime } from '../../lib/format';
import { PageHeader } from '../../pages/PageHeader';
import { useVisite } from '../visite/VisiteGuidee';

import { CodesSecours, Enrolement, FormulaireCode } from './DoubleAuth';

/**
 * Mon compte : la double authentification de l'admin connecté (cahier des
 * charges §9) — activer, relier un nouveau téléphone, renouveler les codes
 * de secours, désactiver quand elle n'est pas obligatoire.
 */
export function MonComptePage() {
  const { user } = useSession();
  const client = useQueryClient();
  const [modale, setModale] = useState<'telephone' | 'codes' | 'desactiver' | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [erreurCodes, setErreurCodes] = useState<string | null>(null);
  const [envoiCodes, setEnvoiCodes] = useState(false);

  const etat = useQuery({ queryKey: ['2fa'], queryFn: () => lire(api.GET('/api/auth/2fa/status')) });
  const rafraichir = () => void client.invalidateQueries({ queryKey: ['2fa'] });
  const e = etat.data;

  const nouveauxCodes = async () => {
    setEnvoiCodes(true);
    setErreurCodes(null);
    try {
      setCodes((await lire(api.POST('/api/auth/2fa/recovery-codes'))).recoveryCodes);
      rafraichir();
    } catch (err) {
      setErreurCodes(messageDe(err));
    } finally {
      setEnvoiCodes(false);
    }
  };

  const fermer = () => {
    setModale(null);
    setCodes(null);
    setErreurCodes(null);
    rafraichir();
  };

  return (
    <>
      <PageHeader title="Mon compte" lead={user?.email} />
      <Section title="Double authentification">
        {etat.isError ? (
          <ErreurChargement erreur={etat.error} onReessayer={() => void etat.refetch()} />
        ) : !e ? (
          <Skeleton height={120} />
        ) : !e.available ? (
          <Alert tone="info">La double authentification n’est pas encore configurée sur le serveur. L’équipe technique doit l’activer.</Alert>
        ) : e.enabled ? (
          <Card className="carte-securite">
            <InfoList
              items={[
                { label: 'État', value: <Badge tone="success">Activée</Badge> },
                { label: 'Depuis le', value: formatDateTime(e.enabledAt) },
                {
                  label: 'Codes de secours',
                  value: e.recoveryCodesLeft > 2 ? `${e.recoveryCodesLeft} restants` : <strong>{e.recoveryCodesLeft} restant{e.recoveryCodesLeft > 1 ? 's' : ''} : renouvelez-les</strong>,
                },
                { label: 'Obligatoire', value: e.required ? 'Oui, pour les super admins' : 'Non' },
              ]}
            />
            <div className="row" style={{ marginTop: 14 }}>
              <Button size="sm" icon={<Smartphone size={14} aria-hidden="true" />} onClick={() => setModale('telephone')}>
                Relier un nouveau téléphone
              </Button>
              <Button size="sm" icon={<KeyRound size={14} aria-hidden="true" />} onClick={() => setModale('codes')}>
                Nouveaux codes de secours
              </Button>
              {!e.required && (
                <Button size="sm" variant="ghost" onClick={() => setModale('desactiver')}>
                  Désactiver
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <Card className="carte-securite">
            <p className="texte-aide" style={{ marginBottom: 14 }}>
              {e.required
                ? 'Obligatoire pour les super admins : activez-la pour continuer à utiliser le back-office.'
                : 'Recommandée : un code du téléphone est demandé en plus du mot de passe à chaque connexion.'}
            </p>
            <Alert tone="info">
              L’ancien back-office ne sait pas demander ce code : une fois la double authentification activée, votre compte ne peut plus
              s’en servir. Utilisez ce back-office.
            </Alert>
            <div style={{ height: 12 }} />
            <Enrolement onTermine={rafraichir} />
          </Card>
        )}
      </Section>

      <ConnexionsMcp />

      <VisiteGuideeCompte />

      <Modal open={modale === 'telephone'} onOpenChange={(o) => !o && fermer()} title="Relier un nouveau téléphone" width={480}>
        <p className="texte-aide">L’ancien téléphone ne donnera plus de codes valables. De nouveaux codes de secours remplacent les anciens.</p>
        <Enrolement remplacement onTermine={fermer} />
      </Modal>

      <Modal open={modale === 'codes'} onOpenChange={(o) => !o && fermer()} title="Nouveaux codes de secours" width={480}>
        {codes ? (
          <div className="stack">
            <Alert tone="info">Les anciens codes ne valent plus. Notez ceux-ci : ils ne seront plus affichés.</Alert>
            <CodesSecours codes={codes} />
            <Button variant="primary" onClick={fermer}>
              J’ai noté mes codes
            </Button>
          </div>
        ) : (
          <div className="stack">
            <p className="texte-aide">Les codes de secours actuels cesseront de fonctionner.</p>
            {erreurCodes && <Alert>{erreurCodes}</Alert>}
            <Button variant="primary" loading={envoiCodes} onClick={() => void nouveauxCodes()}>
              Générer 10 nouveaux codes
            </Button>
          </div>
        )}
      </Modal>

      <Modal open={modale === 'desactiver'} onOpenChange={(o) => !o && fermer()} title="Désactiver la double authentification" width={440}>
        <p className="texte-aide">Seul le mot de passe sera demandé à la connexion. Confirmez avec un code de l’application.</p>
        <FormulaireCode
          bouton="Désactiver"
          onValider={async (code) => {
            await desactiverDoubleAuth(code);
            fermer();
          }}
        />
      </Modal>
    </>
  );
}

/**
 * Assistant IA (MCP) : adresse à ajouter dans Claude, et applications déjà
 * connectées avec ce compte, révocables tout de suite.
 */
function ConnexionsMcp() {
  const client = useQueryClient();
  const toast = useToast();
  const [retirer, setRetirer] = useState<{ id: string; application: string } | null>(null);
  const connexions = useQuery({ queryKey: ['mcp-connexions'], queryFn: async () => (await lire(api.GET('/api/admin/mcp/connexions'))).connexions });
  const adresse = `${API_URL.replace(/\/+$/, '')}/mcp`;

  return (
    <Section title="Assistant IA (MCP)">
      <Card className="carte-securite">
        <div className="stack">
          <p className="texte-aide" style={{ margin: 0 }}>
            Connectez Claude au back-office : il pourra consulter les données et faire, avec votre accord et un motif, les actions permises à
            votre rôle. Chaque action est inscrite au journal d’audit avec la mention « MCP ».
          </p>
          <ol className="etapes">
            <li>Dans Claude : Paramètres, Connecteurs, « Ajouter un connecteur personnalisé ».</li>
            <li>
              Collez cette adresse : <Code valeur={adresse} />
            </li>
            <li>Connectez-vous avec votre compte du back-office, puis cliquez sur « Autoriser ».</li>
          </ol>
          {connexions.isError ? (
            <ErreurChargement erreur={connexions.error} onReessayer={() => void connexions.refetch()} />
          ) : !connexions.data ? (
            <Skeleton height={40} />
          ) : connexions.data.length === 0 ? (
            <p className="note" style={{ margin: 0 }}>
              <Bot size={13} aria-hidden="true" /> Aucune application connectée avec votre compte.
            </p>
          ) : (
            <ul className="connexions-mcp" aria-label="Applications connectées">
              {connexions.data.map((c) => (
                <li key={c.id}>
                  <div>
                    <strong>{c.application}</strong>
                    <div className="cell-sub">
                      Autorisée le {formatDateTime(c.createdAt)} · {c.lastUsedAt ? `utilisée le ${formatDateTime(c.lastUsedAt)}` : 'pas encore utilisée'}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" icon={<Unplug size={14} aria-hidden="true" />} onClick={() => setRetirer({ id: c.id, application: c.application })}>
                    Retirer l’accès
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
      <ConfirmDialog
        open={!!retirer}
        onOpenChange={(o) => !o && setRetirer(null)}
        title="Retirer l’accès"
        confirmLabel="Retirer"
        tone="danger"
        onConfirm={async () => {
          if (!retirer) return;
          await lire(api.DELETE('/api/admin/mcp/connexions/{id}', { params: { path: { id: retirer.id } } }));
          toast.succes(`Accès de ${retirer.application} retiré.`);
          void client.invalidateQueries({ queryKey: ['mcp-connexions'] });
        }}
      >
        <p>{retirer?.application} ne pourra plus rien lire ni faire sur le back-office. Pour le reconnecter, il faudra l’autoriser de nouveau.</p>
      </ConfirmDialog>
    </Section>
  );
}

/** Visite guidée : relancée à la demande (elle s'est lancée seule à la première connexion). */
function VisiteGuideeCompte() {
  const { lancer } = useVisite();
  return (
    <Section title="Visite guidée">
      <Card className="carte-securite">
        <p className="texte-aide" style={{ marginBottom: 14 }}>
          Un tour rapide du back-office : où se trouve chaque écran, et ce qu’on y fait. Il s’est lancé à votre première connexion ; vous
          pouvez le revoir quand vous voulez.
        </p>
        <Button size="sm" icon={<Compass size={14} aria-hidden="true" />} onClick={lancer}>
          Relancer la visite
        </Button>
      </Card>
    </Section>
  );
}
